import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Loader, ArrowLeft, Plus, FileText, ScrollText, Upload, FileCheck2, Mail, X, LayoutGrid,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import api, { getErrorMessage } from "../lib/api";
import { fmt } from "../lib/utils";
import Avatar from "./common/Avatar";
import StatusBadge from "./common/StatusBadge";
import Modal from "./common/Modal";
import KpiCard from "./common/KpiCard";
import RiskDonut from "./common/RiskDonut";
import AreaChartFlow from "./common/AreaChartFlow";

type Tab = "oversikt" | "fakturaer" | "kontrakter";

function ConsumptionBar({ label, fakturert, value, pct, currency }:
  { label: string; fakturert: number; value: number; pct: number | null; currency: string }) {
  const over = pct != null && pct > 100;
  const width = pct == null ? 0 : Math.min(pct, 100);
  return (
    <div>
      <div className="flex items-center justify-between text-[12.5px] mb-1.5 gap-2">
        <span className="font-semibold text-night truncate">{label}</span>
        <span className={`tabular shrink-0 ${over ? "text-neg font-bold" : "text-muted"}`}>{pct != null ? `${pct}%` : "—"}</span>
      </div>
      <div className="h-2 rounded-full bg-black/[0.06] overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${width}%`, background: over ? "#DC2626" : "#16A34A" }} />
      </div>
      <p className="text-[11.5px] text-faint tabular mt-1">{fmt(fakturert, currency)} / {fmt(value, currency)}</p>
    </div>
  );
}

export default function ProjectDetailView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [project, setProject] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [overview, setOverview] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("oversikt");

  const [showVendor, setShowVendor] = useState(false);
  const [vendorForm, setVendorForm] = useState({ vendor_name: "", contract_ref: "", contract_value: "", currency: "NOK", contract_type: "NS8405" });
  const [savingVendor, setSavingVendor] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [emailInput, setEmailInput] = useState<Record<string, string>>({});
  const [emailBusy, setEmailBusy] = useState<string | null>(null);

  const user = JSON.parse(localStorage.getItem("contrack_user") || "null");
  const canEdit = user?.role === "admin" || user?.role === "cfo";

  const loadProject = useCallback(async () => {
    try {
      const r = await api.get("/projects/");
      const p = (r.data.projects || []).find((x: any) => x.id === id);
      setProject(p || null);
    } catch { setProject(null); }
  }, [id]);

  const loadInvoices = useCallback(async () => {
    try { const r = await api.get(`/portfolio/project/${id}/invoices`); setInvoices(r.data.invoices || []); }
    catch { setInvoices([]); }
  }, [id]);

  const loadOverview = useCallback(async () => {
    try { const r = await api.get(`/portfolio/project/${id}/overview`); setOverview(r.data); }
    catch { setOverview(null); }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadProject(), loadInvoices(), loadOverview()]).finally(() => setLoading(false));
  }, [loadProject, loadInvoices, loadOverview]);

  const addVendor = async () => {
    if (!vendorForm.vendor_name.trim()) { setError(t("detail.vendor_required")); return; }
    setSavingVendor(true); setError(null);
    try {
      await api.post(`/projects/${id}/vendors`, { ...vendorForm, contract_value: vendorForm.contract_value ? Number(vendorForm.contract_value) : null });
      setShowVendor(false);
      setVendorForm({ vendor_name: "", contract_ref: "", contract_value: "", currency: "NOK", contract_type: "NS8405" });
      await Promise.all([loadProject(), loadOverview()]);
    } catch (e: any) { setError(getErrorMessage(e, t("detail.vendor_failed"))); }
    finally { setSavingVendor(false); }
  };

  const addEmail = async (contractId: string) => {
    const email = (emailInput[contractId] || "").trim();
    if (!email) return;
    setEmailBusy(contractId);
    try { await api.post("/portfolio/leverandor/legg-til-email", { contract_id: contractId, email }); setEmailInput(s => ({ ...s, [contractId]: "" })); await loadProject(); }
    catch { /* ignore */ } finally { setEmailBusy(null); }
  };
  const removeEmail = async (contractId: string, email: string) => {
    setEmailBusy(contractId);
    try { await api.post("/portfolio/leverandor/fjern-email", { contract_id: contractId, email }); await loadProject(); }
    catch { /* ignore */ } finally { setEmailBusy(null); }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-32"><Loader className="w-6 h-6 animate-spin text-faint" /></div>
  );
  if (!project) return (
    <div className="cx-card p-12 text-center">
      <p className="text-[15px] font-bold text-night">{t("detail.not_found")}</p>
      <button onClick={() => navigate("/projects")} className="cx-btn-ghost mx-auto mt-4">{t("detail.back")}</button>
    </div>
  );

  const vendors = project.vendors || [];
  const sm = overview?.summary || {};
  const st = overview?.status || { godkjent: 0, venter: 0, flagget: 0 };
  const currency = overview?.currency || project.currency || "NOK";
  const totalStatus = (st.godkjent || 0) + (st.venter || 0) + (st.flagget || 0);

  const tabs: { key: Tab; label: string; icon: any; count?: number }[] = [
    { key: "oversikt", label: t("detail.overview"), icon: LayoutGrid },
    { key: "fakturaer", label: t("nav.fakturaer"), icon: FileText, count: invoices.length },
    { key: "kontrakter", label: t("nav.kontrakter"), icon: ScrollText, count: vendors.length },
  ];

  return (
    <div className="animate-fade-up">
      <button onClick={() => navigate("/projects")} className="inline-flex items-center gap-1.5 text-[13px] text-muted hover:text-night transition-colors mb-4">
        <ArrowLeft className="w-4 h-4" /> {t("nav.prosjekter")}
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className="text-greeting text-night">{project.name}</h1>
            <span className="cx-chip bg-black/[0.05] text-muted">{project.contract_type}</span>
          </div>
          <p className="text-[13px] text-muted mt-1">{t("detail.summary2", { invoices: invoices.length, contracts: vendors.length })}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-line mb-5">
        {tabs.map(tb => {
          const Icon = tb.icon;
          const active = tab === tb.key;
          return (
            <button key={tb.key} onClick={() => setTab(tb.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-2.5 text-[13.5px] font-semibold border-b-2 -mb-px transition-colors ${active ? "text-night border-night" : "text-muted border-transparent hover:text-night"}`}>
              <Icon className="w-4 h-4" /> {tb.label}
              {tb.count != null && <span className={`text-[11px] font-bold px-1.5 py-0.5 rounded-md ${active ? "bg-night/[0.07] text-night" : "bg-black/[0.04] text-faint"}`}>{tb.count}</span>}
            </button>
          );
        })}
      </div>

      {/* ── OVERSIKT ── */}
      {tab === "oversikt" && (
        <div className="space-y-5">
          {/* KPI */}
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            <KpiCard label={t("detail.kpi_contract_value")} value={fmt(sm.kontraktsverdi || 0, currency)} />
            <KpiCard label={t("detail.kpi_invoiced")} value={fmt(sm.fakturert || 0, currency)}
              sub={sm.forbruk_pct != null ? t("detail.kpi_invoiced_sub", { pct: sm.forbruk_pct }) : undefined} />
            <KpiCard label={t("detail.kpi_remaining")} value={fmt(sm.gjenstaaende || 0, currency)}
              delta={sm.gjenstaaende < 0 ? { label: t("detail.over_budget"), direction: "down", tone: "neg" } : null} />
            <KpiCard label={t("detail.kpi_deviation")} value={fmt(sm.avvik || 0, currency)}
              delta={st.flagget > 0 ? { label: t("portfolio.fakturaer_count", { count: st.flagget }), direction: "up", tone: "neg" } : null} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {/* Kontraktsforbruk */}
            <div className="cx-card p-5 lg:col-span-2">
              <h3 className="text-[15px] font-bold text-night">{t("detail.consumption")}</h3>
              <p className="text-[12.5px] text-muted mt-0.5 mb-4">{t("detail.consumption_hint")}</p>
              {(overview?.contracts || []).length === 0 ? (
                <p className="text-[13px] text-faint py-4">{t("detail.no_contracts")}</p>
              ) : (
                <div className="space-y-4">
                  {(overview.contracts || []).map((c: any) => (
                    <ConsumptionBar key={c.contract_id} label={c.vendor_name} fakturert={c.fakturert} value={c.contract_value} pct={c.pct} currency={currency} />
                  ))}
                </div>
              )}
            </div>

            {/* Fakturastatus */}
            <div className="cx-card p-5 flex flex-col">
              <h3 className="text-[15px] font-bold text-night mb-1">{t("detail.status_title")}</h3>
              <p className="text-[12.5px] text-muted mb-2">{t("detail.status_hint")}</p>
              {totalStatus === 0 ? (
                <p className="text-[13px] text-faint py-8 text-center">{t("detail.no_invoices")}</p>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center">
                  <RiskDonut size={150} centerLabel={totalStatus} centerSub={t("portfolio.fakturaer")}
                    segments={[
                      { name: t("portfolio.godkjent"), value: st.godkjent, color: "#16A34A" },
                      { name: t("portfolio.venter"), value: st.venter, color: "#D97706" },
                      { name: t("portfolio.flagget"), value: st.flagget, color: "#DC2626" },
                    ]} />
                  <div className="grid grid-cols-3 gap-2 w-full mt-4 text-center">
                    <div><p className="text-[18px] font-bold text-pos tabular leading-none">{st.godkjent}</p><p className="text-[10.5px] font-semibold uppercase text-faint mt-1">{t("portfolio.godkjent")}</p></div>
                    <div><p className="text-[18px] font-bold text-flag tabular leading-none">{st.venter}</p><p className="text-[10.5px] font-semibold uppercase text-faint mt-1">{t("portfolio.venter")}</p></div>
                    <div><p className="text-[18px] font-bold text-neg tabular leading-none">{st.flagget}</p><p className="text-[10.5px] font-semibold uppercase text-faint mt-1">{t("portfolio.flagget")}</p></div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Fakturaflyt */}
          <AreaChartFlow projectId={id} currency={currency} />
        </div>
      )}

      {/* ── Fakturaer ── */}
      {tab === "fakturaer" && (
        invoices.length === 0 ? (
          <div className="cx-card p-12 text-center">
            <FileText className="w-7 h-7 text-faint mx-auto mb-3" />
            <p className="text-[14px] font-semibold text-night">{t("detail.no_invoices")}</p>
            <p className="text-[12.5px] text-muted mt-1">{t("detail.no_invoices_sub")}</p>
          </div>
        ) : (
          <div className="cx-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-line">
                  <th className="cx-th">{t("recent.col_customer")}</th>
                  <th className="cx-th">{t("recent.col_invoice")}</th>
                  <th className="cx-th text-right">{t("recent.col_amount")}</th>
                  <th className="cx-th">{t("recent.col_status")}</th>
                </tr></thead>
                <tbody>
                  {invoices.map((inv: any, i: number) => (
                    <tr key={inv.id} onClick={() => navigate(`/invoices?project=${id}&invoice=${inv.id}`)}
                      className={`${i > 0 ? "border-t border-line" : ""} cursor-pointer hover:bg-canvas transition-colors`}>
                      <td className="cx-td">
                        <div className="flex items-center gap-2.5">
                          <Avatar name={inv.vendor_name || "?"} size="sm" />
                          <span className="font-semibold truncate max-w-[200px]">{inv.vendor_name || "—"}</span>
                        </div>
                      </td>
                      <td className="cx-td text-muted tabular">{inv.invoice_number || "—"}</td>
                      <td className="cx-td text-right font-bold tabular">{fmt(inv.total_amount, inv.currency)}</td>
                      <td className="cx-td"><StatusBadge status={inv.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      )}

      {/* ── Kontrakter ── */}
      {tab === "kontrakter" && (
        <div>
          {canEdit && (
            <div className="flex justify-end mb-4">
              <button onClick={() => { setError(null); setShowVendor(true); }} className="cx-btn-dark">
                <Plus className="w-4 h-4" /> {t("detail.add_vendor")}
              </button>
            </div>
          )}
          {vendors.length === 0 ? (
            <div className="cx-card p-12 text-center">
              <ScrollText className="w-7 h-7 text-faint mx-auto mb-3" />
              <p className="text-[14px] font-semibold text-night">{t("detail.no_contracts")}</p>
              <p className="text-[12.5px] text-muted mt-1">{t("detail.no_contracts_sub")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {vendors.map((v: any) => (
                <div key={v.contract_id} className="cx-card p-5">
                  <div className="flex items-center gap-3">
                    <Avatar name={v.vendor_name || "?"} size="md" />
                    <div className="min-w-0 flex-1">
                      <h3 className="text-[15px] font-bold text-night truncate">{v.vendor_name}</h3>
                      <p className="text-[12px] text-muted">{v.contract_ref || t("detail.no_ref")} · {v.contract_type || project.contract_type}</p>
                    </div>
                    {v.has_contract && <FileCheck2 className="w-4 h-4 text-pos shrink-0" />}
                  </div>

                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-line">
                    <div>
                      <p className="text-[11px] text-faint font-medium">{t("suppliers.contract_value")}</p>
                      <p className="text-[15px] font-bold text-night tabular mt-0.5">{fmt(v.contract_value, v.currency || project.currency)}</p>
                    </div>
                    {canEdit && (
                      <label className="cx-btn-ghost cursor-pointer">
                        <Upload className="w-3.5 h-3.5" /> {v.has_contract ? t("detail.replace_contract") : t("detail.upload_contract")}
                        <input type="file" accept=".pdf,.docx" className="hidden"
                          onChange={async (e) => {
                            const f = e.target.files?.[0]; if (!f) return;
                            setError(null);
                            try {
                              const fd = new FormData(); fd.append("file", f);
                              await api.post(`/projects/${id}/vendors/${v.contract_id}/upload-contract`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                              await Promise.all([loadProject(), loadOverview()]);
                            } catch (err: any) { setError(getErrorMessage(err, t("detail.upload_failed"))); }
                          }} />
                      </label>
                    )}
                  </div>

                  {/* Prismodell (A3) */}
                  {canEdit && (
                    <div className="mt-4 pt-4 border-t border-line flex items-center justify-between gap-3">
                      <p className="text-[11.5px] font-semibold uppercase tracking-[0.04em] text-faint">{t("detail.pricing_model")}</p>
                      <select value={v.pricing_model || ""} className="field-ios !py-1.5 !text-[12.5px] !w-auto"
                        onChange={async (e) => {
                          try { await api.patch(`/projects/${id}/contracts/${v.contract_id}`, { pricing_model: e.target.value }); await loadProject(); }
                          catch (err: any) { setError(getErrorMessage(err)); }
                        }}>
                        <option value="">{t("detail.pricing_unset")}</option>
                        <option value="unit_price">{t("detail.pricing_unit")}</option>
                        <option value="fixed_sum_installments">{t("detail.pricing_fixed")}</option>
                        <option value="cost_plus">{t("detail.pricing_cost")}</option>
                      </select>
                    </div>
                  )}

                  {/* Godkjente avsendere */}
                  <div className="mt-4 pt-4 border-t border-line">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Mail className="w-3.5 h-3.5 text-faint" />
                      <p className="text-[11.5px] font-semibold uppercase tracking-[0.04em] text-faint">{t("detail.approved_senders")}</p>
                    </div>
                    <p className="text-[11.5px] text-muted mb-2.5">{t("detail.approved_senders_hint")}</p>
                    {(v.approved_emails || []).length === 0 ? (
                      <p className="text-[12.5px] text-faint mb-2.5">{t("detail.no_approved_emails")}</p>
                    ) : (
                      <div className="flex flex-wrap gap-1.5 mb-2.5">
                        {(v.approved_emails || []).map((em: string) => (
                          <span key={em} className="inline-flex items-center gap-1 text-[12px] font-medium bg-canvas border border-line rounded-[7px] pl-2 pr-1 py-1 text-night">
                            {em}
                            {canEdit && (
                              <button onClick={() => removeEmail(v.contract_id, em)} disabled={emailBusy === v.contract_id}
                                className="p-0.5 rounded text-faint hover:text-neg hover:bg-neg/10 transition-colors"><X className="w-3 h-3" /></button>
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <input type="email" value={emailInput[v.contract_id] || ""}
                          onChange={e => setEmailInput(s => ({ ...s, [v.contract_id]: e.target.value }))}
                          onKeyDown={e => { if (e.key === "Enter") addEmail(v.contract_id); }}
                          placeholder={t("detail.email_ph")} className="field-ios !py-2 !text-[13px] flex-1" />
                        <button onClick={() => addEmail(v.contract_id)} disabled={emailBusy === v.contract_id || !(emailInput[v.contract_id] || "").trim()} className="cx-btn-dark !py-2 shrink-0">
                          {emailBusy === v.contract_id ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                          {t("detail.add_email")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {error && tab === "kontrakter" && <p className="text-[13px] text-neg mt-3">{error}</p>}
        </div>
      )}

      {/* Legg til leverandør-modal */}
      <Modal open={showVendor} onClose={() => setShowVendor(false)} title={t("detail.add_vendor")} subtitle={project.name}>
        <div className="space-y-4">
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("detail.vendor_name")}</label>
            <input value={vendorForm.vendor_name} onChange={e => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
              placeholder={t("detail.vendor_name_ph")} className="field-ios" autoFocus />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("detail.contract_ref")}</label>
              <input value={vendorForm.contract_ref} onChange={e => setVendorForm({ ...vendorForm, contract_ref: e.target.value })} placeholder="VEK-2024-047" className="field-ios" />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("suppliers.contract_value")}</label>
              <input value={vendorForm.contract_value} onChange={e => setVendorForm({ ...vendorForm, contract_value: e.target.value })} placeholder="14750000" type="number" className="field-ios" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("projects.currency_label")}</label>
              <select value={vendorForm.currency} onChange={e => setVendorForm({ ...vendorForm, currency: e.target.value })} className="field-ios">
                <option value="NOK">NOK</option><option value="USD">USD</option><option value="EUR">EUR</option><option value="AED">AED</option>
              </select>
            </div>
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("projects.contract_type_label")}</label>
              <select value={vendorForm.contract_type} onChange={e => setVendorForm({ ...vendorForm, contract_type: e.target.value })} className="field-ios">
                <option value="NS8405">NS 8405</option><option value="NS8407">NS 8407</option><option value="FIDIC">FIDIC</option>
              </select>
            </div>
          </div>
          {error && <p className="text-[13px] text-neg">{error}</p>}
          <button onClick={addVendor} disabled={savingVendor} className="cx-btn-dark w-full py-3">
            {savingVendor ? <Loader className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {t("detail.add_vendor")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
